/**
 * Composants UI pour VeillePage
 * Badges de statut et de type pour la liste des articles
 */
import React from 'react';
import { Loader2, Rss, Radio, Crosshair } from 'lucide-react';

// Configuration des types de sources
export const TYPE_CONFIG = {
  rss: {
    label: 'Radar',
    icon: Rss,
    color: 'cyan',
    description: 'Flux RSS/Atom',
    sourcesLabel: 'Flux RSS',
    urlPlaceholder: 'https://www.lemonde.fr/rss_full.xml',
  },
  page: {
    label: 'Sonar',
    icon: Radio,
    color: 'purple',
    description: 'Pages web',
    sourcesLabel: 'Pages surveillées',
    urlPlaceholder: 'https://www.example.com/actualites',
  },
  keywords: {
    label: 'Cible',
    icon: Crosshair,
    color: 'amber',
    description: 'Mots-clés',
    sourcesLabel: 'Cibles',
    urlPlaceholder: 'https://www.example.com/page-a-surveiller',
  },
};

/**
 * Badge de statut d'un article
 */
export const StatusBadge = ({ article }) => {
  if (article.publish_status === 'published') {
    return (
      <span className="text-[10px] bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded font-mono">
        Publié
      </span>
    );
  }
  if (article.publish_status === 'partial') {
    return (
      <span className="text-[10px] bg-orange-500/15 text-orange-400 px-1.5 py-0.5 rounded font-mono">
        Partiel
      </span>
    );
  }
  if (article.generation_status === 'done') {
    return (
      <span className="text-[10px] bg-green-500/15 text-green-400 px-1.5 py-0.5 rounded font-mono">
        Généré
      </span>
    );
  }
  if (article.generation_status === 'generating') {
    return (
      <span className="text-[10px] bg-blue-500/15 text-blue-400 px-1.5 py-0.5 rounded font-mono flex items-center gap-1">
        <Loader2 className="w-2 h-2 animate-spin" />
        Traitement...
      </span>
    );
  }
  if (article.scrape_status === 'done') {
    return (
      <span className="text-[10px] bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded font-mono">
        Scrapé
      </span>
    );
  }
  if (article.scrape_status === 'scraping') {
    return (
      <span className="text-[10px] bg-blue-500/15 text-blue-400 px-1.5 py-0.5 rounded font-mono flex items-center gap-1">
        <Loader2 className="w-2 h-2 animate-spin" />
        Scraping...
      </span>
    );
  }
  return null;
};

/**
 * Badge de type de source (Radar/Sonar/Cible)
 */
export const TypeBadge = ({ type }) => {
  const cfg = TYPE_CONFIG[type];
  if (!cfg) return null;
  const Icon = cfg.icon;
  
  // Classes Tailwind dynamiques pour les couleurs
  const colorClasses = {
    cyan: 'bg-cyan-500/15 text-cyan-400',
    purple: 'bg-purple-500/15 text-purple-400',
    amber: 'bg-amber-500/15 text-amber-400',
  };
  
  return (
    <span className={`text-[10px] ${colorClasses[cfg.color]} px-1.5 py-0.5 rounded font-mono flex items-center gap-1`}>
      <Icon className="w-2.5 h-2.5" />
      {cfg.label}
    </span>
  );
};

/**
 * Indicateur de période de filtre
 */
export const PeriodFilter = ({ value, onChange, options }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-slate-500">Période :</span>
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-300"
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

/**
 * Bouton de filtre par type
 */
export const TypeFilterButton = ({ type, config, isActive, count, onClick }) => {
  const Icon = config.icon;
  const colorClasses = {
    cyan: {
      active: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50',
      inactive: 'text-slate-400 border-slate-700 hover:border-cyan-500/30'
    },
    purple: {
      active: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
      inactive: 'text-slate-400 border-slate-700 hover:border-purple-500/30'
    },
    amber: {
      active: 'bg-amber-500/20 text-amber-400 border-amber-500/50',
      inactive: 'text-slate-400 border-slate-700 hover:border-amber-500/30'
    }
  };
  
  const colors = colorClasses[config.color];
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all text-sm ${
        isActive ? colors.active : colors.inactive
      }`}
    >
      <Icon className="w-4 h-4" />
      <span>{config.label}</span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
        isActive ? 'bg-white/10' : 'bg-slate-800'
      }`}>
        {count}
      </span>
    </button>
  );
};
